import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { TFunction } from 'i18next';
import * as yup from 'yup';

import {
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Text,
  TextInputField,
} from '@electricjs/arc';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useUpdateAssetMutation } from '@/redux/slices/assetApiSlice';

type EditAssetFormData = {
  name: string;
  serialNumber: string;
};

const getEditAssetSchema = (t: TFunction, serialNumberOnly: boolean) =>
  yup.object<EditAssetFormData>().shape({
    // `.trim()` transforms whitespace-only input to an empty string before
    // `.required()` runs, so a name/serial number of e.g. " " is rejected
    // instead of passing validation as if it were meaningful input.
    //
    // The name rule has to be relaxed in serial-number-only mode: Save is
    // gated on `isValid`, so an asset with no name would leave Save disabled
    // forever with no field on screen to fix it.
    name: serialNumberOnly
      ? yup.string()
      : yup.string().trim().required(t('assetSchema.nameRequired')),
    serialNumber: yup
      .string()
      .trim()
      .required(t('assetSchema.serialNumberRequired')),
  });

type EditAssetModalProps = {
  assetId: string;
  name: string;
  serialNumber?: string;
  // Restricts the modal to the serial number field, for callers that only
  // need the serial filled in (e.g. the offboarding device-retrieval step).
  serialNumberOnly?: boolean;
  onClose: () => void;
  // Called with the fields the user just saved, so the caller (e.g. the
  // ship-laptop page) can update its already-selected asset option in place
  // without waiting on a re-fetch.
  onAssetUpdated: (updated: { name: string; serialNumber: string }) => void;
};

export const EditAssetModal = ({
  assetId,
  name,
  serialNumber,
  serialNumberOnly = false,
  onClose,
  onAssetUpdated,
}: EditAssetModalProps) => {
  const { t } = useTranslation('assets');
  const { showErrorToast, showSuccessToast } = useGlobalUI();
  const organizationId = useGetOrganizationId();

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<EditAssetFormData>({
    resolver: yupResolver(getEditAssetSchema(t, serialNumberOnly)),
    mode: 'onTouched',
    defaultValues: {
      name,
      serialNumber: serialNumber ?? '',
    },
  });

  const [updateAsset, { isLoading }] = useUpdateAssetMutation();

  const onSubmit = (data: EditAssetFormData) => {
    updateAsset({
      organizationId,
      assetId,
      serialNumber: data.serialNumber,
      // The asset PATCH is a partial update, so omitting the name leaves it
      // untouched rather than clearing it.
      ...(serialNumberOnly ? {} : { name: data.name }),
    })
      .unwrap()
      .then(() => {
        showSuccessToast({
          id: 'edit-asset-success-toast',
          message: t('editAssetModal.successMessage'),
        });
        onAssetUpdated(data);
        onClose();
      })
      .catch(error => {
        console.error('Error while updating asset: ', error);
        showErrorToast({
          id: 'edit-asset-error-toast',
          message: t('editAssetModal.errorMessage'),
        });
      });
  };

  return (
    <ModalV2
      ariaLabelledby="edit-asset-modal-header-text"
      hide={onClose}
      maxWidth="60rem"
      visible>
      <ModalHeader>
        <Text id="edit-asset-modal-header-text" variant="heading-2">
          {t('editAssetModal.title')}
        </Text>
      </ModalHeader>
      <ModalBody>
        <form>
          {!serialNumberOnly && (
            <TextInputField
              id="edit-asset-name"
              name="name"
              control={control}
              label={t('newAssetForm.name')}
              required
            />
          )}
          <TextInputField
            id="edit-asset-serial-number"
            name="serialNumber"
            control={control}
            label={t('editAssetModal.serialNumberLabel')}
            helperText={t(
              serialNumberOnly
                ? 'editAssetModal.serialNumberHelperTextOffboarding'
                : 'editAssetModal.serialNumberHelperText'
            )}
            required
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction
            id="edit-asset-modal-cancel-button"
            testId="edit-asset-modal-cancel-button"
            variant="text"
            disabled={isLoading}
            onClick={onClose}>
            {t('newAssetForm.cancel')}
          </ModalAction>
          <ModalAction
            id="edit-asset-modal-save-button"
            testId="edit-asset-modal-save-button"
            loading={isLoading}
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}>
            {t('editAssetModal.saveButton')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default EditAssetModal;
